<template>
    <div>
        <h1 class="display-heading">Prescription Detail</h1>
        <div class="card mb-5">
            <div class="card-body">
                <prescription-summary :prescriptionId="prescriptionDetails.prescriptionId"
                    :doctorName="prescriptionDetails.doctorName"
                    :patientName="prescriptionDetails.patientName"
                    :prescriptionDate="prescriptionDetails.prescriptionDate"
                    :prescriptionValidUntil="prescriptionDetails.prescriptionValidUntil"
                    :prescriptionSpecialReminder="prescriptionDetails.prescriptionSpecialReminder"
                    :prescriptionType="prescriptionDetails.prescriptionType"
                    :prescriptionStatus="prescriptionDetails.prescriptionStatus"
                    :isUploadedPrescription="uploadedPrescription.isUpload"
                    :uploadedFileId="uploadedPrescription.uploadFileId"
                    :uploadRemarks="uploadedPrescription.uploadRemarks"
                    :isDelivered="isPrescriptionDelivered"
                    :deliveryProvider="prescriptionDelivery.deliveryProvider"
                    :deliveryTrackingCode="prescriptionDelivery.trackingCode"
                    :deliveryTrackingWebAddress="prescriptionDelivery.trackingUrl"
                    :attachedDeliveryPhoto="prescriptionDelivery.trackingAttachment"
                    :isPharmacy="true">
                </prescription-summary>
            </div>
        </div>

        <div v-if="enablePrescriptionStatusUpdate && !isPrescriptionDelivered" class="mb-5">
            <h5 class="mb-3">Update Prescription Status</h5>
            <prescription-status-update ref="prescriptionUpdate" :status="prescriptionDetails.prescriptionStatusDisplay" v-model="prescriptionStatusUpdate" :statusId="prescriptionStatusId">
                <p v-if="prescriptionStatusUpdate.updateMessage" :class="{
                    'text-danger': prescriptionStatusUpdate.hasError
                }">
                    {{prescriptionStatusUpdate.updateMessage}}
                </p>
                <button type="button" class="btn btn-primary btn-primary-insv" @click="saveStatus">
                    Update Prescription Status
                </button>
            </prescription-status-update>
        </div>

        <div class="mb-5">
            <h5 class="mb-3">Prescription Details</h5>
            <prescription-detail :isEdit="isPrescriptionNeedsReview"
                :pharmacyId="pharmacyId"
                v-model="prescriptionList">
                <template v-if="prescriptionList.length > 0" slot="additional-content">
                    <form data-vv-scope="prescription-valid-days" @submit.prevent="validatePrescriptionValidDays">
                        <div id="prescription" class="d-flex align-items-baseline">

                            <label class="mr-2 mb-0">{{ isPrescriptionNeedsReview ? "Prescription Valid for (Days):" : "Prescription valid until"}}  </label>

                            <div v-if="isPrescriptionNeedsReview" style="max-width: 200px;">
                                <input name="prescriptionValidDays" type="text"
                                    data-vv-validate-on="blur" data-vv-as="prescription valid"
                                    v-validate="'required|numeric|min_value:1'"
                                    v-model="prescriptionValidDays" :class="{
                                    'form-control': true,
                                    'is-invalid':  validationErrors.has('prescription-valid-days.prescriptionValidDays')
                                }">
                            <div v-show="validationErrors.has('prescription-valid-days.prescriptionValidDays')" class="invalid-feedback">
                                {{ validationErrors.first('prescription-valid-days.prescriptionValidDays') }}
                            </div>
                            </div>
                            <div v-else>
                                {{prescriptionDetails.prescriptionValidUntil ? convertToUserReadableDate(prescriptionDetails.prescriptionValidUntil): '-'}}
                            </div>
                        </div>
                    </form>
                </template>
                <template v-if="isPrescriptionNeedsReview" slot="footer">
                    <div class="mt-3">
                        <p v-show="prescriptionSaveMessageIsShown" :class="{
                            'text-danger': prescriptionSaveMessageIsError
                        }">
                            {{prescriptionSaveMessage}}
                        </p>
                        <button type="button" class="btn btn-primary btn-primary-insv" @click="saveAndUpdateMedicineList">
                            Save and Update Medicine List
                        </button>
                    </div>
                </template>
            </prescription-detail>
        </div>

        <div v-if="shouldShowMailingDetails" class="mb-5">
            <h5 class="mb-3">Prescription Delivery</h5>
            <div class="card" style="border-color: red;">
                <div class="card-body">
                    <div class="component-detail">
                        <div class="component-detail-row">
                            <div class="component-detail-title">
                                <b>Prescription ID</b>
                            </div>
                            <div class="component-detail-content">
                                {{prescriptionPatientDeliveryDetails.prescriptionId}}
                            </div>
                        </div>
                        <div class="component-detail-row">
                            <div class="component-detail-title">
                                <b>Patient Name</b>
                            </div>
                            <div class="component-detail-content">
                                {{prescriptionPatientDeliveryDetails.name}}
                            </div>
                        </div>
                        <div class="component-detail-row">
                            <div class="component-detail-title">
                                <b>Delivery Address</b>
                            </div>
                            <div class="component-detail-content">
                                {{prescriptionPatientDeliveryDetails.address}}
                            </div>
                        </div>
                        <div class="component-detail-row">
                            <div class="component-detail-title">
                                <b>Contact Number</b>
                            </div>
                            <div class="component-detail-content">
                                {{prescriptionPatientDeliveryDetails.phoneNumber}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <button class="btn btn-primary btn-primary-insv" type="button" @click="backToPreviousPage">Back to List</button>
        </div>
    </div>
</template>

<script>
import {
    convertToUserReadableDate,
    constructPrescriptionList
} from '@/utilities';
import prescriptionDetail from '@/components/prescription';
import prescriptionSummary from '@/components/prescription/summary';
import prescriptionStatusUpdate from '@/components/prescription/status-update';
export default {
    'name': 'PrescriptionDetailUser',
    'components': {
        prescriptionSummary,
        prescriptionDetail,
        prescriptionStatusUpdate
    },
    'computed': {
        prescriptionId () {
            return this.$route.params.id;
        },
        enablePrescriptionStatusUpdate () {
            return this.prescriptionStatusId === 1000 || this.prescriptionStatusId === 3 || this.prescriptionStatusId === 4;
        },
        isPrescriptionNeedsReview () {
            return this.prescriptionStatusId === 1;
        },
        isPrescriptionDelivered () {
            return this.prescriptionStatusId === 4 || this.prescriptionStatusId === 100;
        },
        shouldShowMailingDetails () {
            return this.prescriptionStatusUpdate.statusId === 3 ||
            this.prescriptionStatusUpdate.statusId === 4 ||
            this.prescriptionStatusId === 3 ||
            this.prescriptionStatusId === 4;
        }
    },
    data () {
        return {
            'prescriptionDetails': {
                'prescriptionId': '',
                'doctorName': '',
                'patientName': '',
                'prescriptionDate': '',
                'prescriptionType': '',
                'prescriptionStatus': 0,
                'prescriptionStatusDisplay': '',
                'prescriptionValidUntil': '',
                'prescriptionSpecialReminder': ''
            },
            'pharmacyId': '',
            'prescriptionList': [],
            'prescriptionValidDays': null,
            'prescriptionSpecialReminder': null,
            'prescriptionStatusUpdate': {
                'statusId': 0,
                'trackingCode': null,
                'deliveryProvider': null,
                'trackingUrl': 'https://sendparcel.poslaju.com.my/open/home',
                'updateMessage': null,
                'hasError': false
            },
            'prescriptionDelivery': {
                'trackingCode': null,
                'deliveryProvider': null,
                'trackingAttachment': null,
                'trackingUrl': null
            },
            'uploadedPrescription': {
                'isUpload': false,
                'uploadFileId': '',
                'uploadRemarks': ''
            },
            'prescriptionStatusId': 0,
            'prescriptionSaveMessage': '',
            'prescriptionSaveMessageIsShown': false,
            'prescriptionSaveMessageIsError': false,
            'prescriptionPatientDeliveryDetails': {
                'name': '',
                'prescriptionId': '',
                'address': '',
                'phoneNumber': ''
            }
        };
    },
    mounted () {
        this.axios.get('Prescription/GetPharmacyPrescription', {
            'params': {
                'prescriptionId': this.prescriptionId
            }
        }).then(({ data }) => {
            let prescription = data.data;
            this.pharmacyId = prescription.pi;
            this.prescriptionDetails.prescriptionDate = prescription.cd;
            this.prescriptionDetails.prescriptionId = prescription.plis;
            this.prescriptionDetails.doctorName = prescription.dn;
            this.prescriptionDetails.patientName = prescription.pn;
            this.prescriptionDetails.prescriptionType = prescription.iu ? 'User Uploaded Prescription' : 'Doctor Consultation';
            this.prescriptionDetails.prescriptionStatus = prescription.ps;
            this.prescriptionDetails.prescriptionStatusDisplay = prescription.s;
            this.prescriptionDetails.prescriptionValidUntil = prescription.vu;
            this.prescriptionDetails.prescriptionSpecialReminder = prescription.sr;

            // set delivery details
            this.prescriptionPatientDeliveryDetails.name = prescription.pn;
            this.prescriptionPatientDeliveryDetails.prescriptionId = prescription.plis;

            // set status update component
            this.prescriptionStatusUpdate.statusId = null;
            this.prescriptionStatusUpdate.trackingCode = prescription.dtc;
            this.prescriptionStatusUpdate.deliveryProvider = prescription.dp;

            // set for delivery status display
            this.prescriptionDelivery.trackingCode = prescription.dtc;
            this.prescriptionDelivery.deliveryProvider = prescription.dp;

            this.prescriptionDelivery.trackingAttachment = prescription.dai;
            this.prescriptionDelivery.trackingUrl = prescription.dtu;

            if (this.prescriptionDelivery.trackingUrl) {
                if (!/^https?:\/\//i.test(this.prescriptionDelivery.trackingUrl)) {
                    this.prescriptionDelivery.trackingUrl = 'http://' + this.prescriptionDelivery.trackingUrl;
                }
            }

            this.prescriptionStatusId = prescription.ps;

            // uploaded
            this.initializePrescriptionUpload(prescription.iu, prescription.pu);

            if (prescription.psr) {
                this.initializePaymentSummary(prescription.psr);
            }

            if (prescription.pil) {
                this.prescriptionList = constructPrescriptionList(prescription.pil);
            }

            if (prescription.pp) {
                this.prescriptionPatientDeliveryDetails.address = `${prescription.pp.mal1}, ${prescription.pp.mal2}, ${prescription.pp.mac}, ${prescription.pp.map}, ${prescription.pp.mas}.`;

                this.prescriptionPatientDeliveryDetails.phoneNumber = prescription.pp.p;
            }
        });
    },
    'methods': {
        convertToUserReadableDate,
        initializePrescriptionUpload (hasUpload, prescriptionUpload) {
            this.uploadedPrescription.isUpload = hasUpload;
            this.uploadedPrescription.uploadFileId = prescriptionUpload ? prescriptionUpload.puid : '';
            this.uploadedPrescription.uploadRemarks = prescriptionUpload ? prescriptionUpload.ur : '';
        },
        saveStatus () {
            this.$refs.prescriptionUpdate.submitForm().then((isValid) => {
                this.prescriptionStatusUpdate.hasError = false;
                this.prescriptionStatusUpdate.updateMessage = 'Updating prescription status...';
                if (isValid) {
                    let formData = new FormData();
                    formData.append('PrescriptionId', this.prescriptionId);
                    formData.append('PrescriptionStatus', this.prescriptionStatusUpdate.statusId);

                    if (this.prescriptionStatusUpdate.statusId === 4) {
                        let uploadedFile = document.querySelector('#uploadFileInput');

                        if (this.prescriptionStatusUpdate.trackingCode) {
                            formData.append('DeliveryTrackingCode', this.prescriptionStatusUpdate.trackingCode);
                        }

                        if (this.prescriptionStatusUpdate.deliveryProvider) {
                            formData.append('DeliveryProvider', this.prescriptionStatusUpdate.deliveryProvider);
                        }

                        if (this.prescriptionStatusUpdate.trackingUrl) {
                            if (!/^https?:\/\//i.test(this.prescriptionStatusUpdate.trackingUrl)) {
                                this.prescriptionStatusUpdate.trackingUrl = 'http://' + this.prescriptionStatusUpdate.trackingUrl;
                            }
                            formData.append('DeliveryTrackingUrl', this.prescriptionStatusUpdate.trackingUrl);
                        }

                        formData.append('DeliveryParcelPhoto', uploadedFile.files[0]);
                    }

                    this.axios.post('Prescription/PharmacyUpdatePrescriptionStatus', formData).then(() => {
                        location.reload();
                    }).catch(() => {
                        this.prescriptionStatusUpdate.hasError = true;
                        this.prescriptionStatusUpdate.updateMessage = 'Error updating prescription status. Please try again.';
                    });
                } else {
                    this.prescriptionStatusUpdate.hasError = true;
                    this.prescriptionStatusUpdate.updateMessage = 'You have error(s) in the form. Please check and retry again.';
                }
            });
        },
        saveAndUpdateMedicineList () {
            this.validatePrescriptionValidDays().then((valid) => {
                this.prescriptionSaveMessageIsShown = true;
                this.prescriptionSaveMessageIsError = false;
                this.prescriptionSaveMessage = '';

                if (valid) {
                    if (this.prescriptionList.length === 0) {
                        this.prescriptionSaveMessageIsError = true;
                        this.prescriptionSaveMessage = 'Medicine list cannot be empty!';
                    } else {
                        let prescriptionList = [];
                        prescriptionList = this.prescriptionList.map((item) => ({
                            'di': item.drugId,
                            'q': item.quantity,
                            'fd': item.consumptionFrequency,
                            'qc': item.quantityPerConsumption,
                            'si': null,
                            'sr': null
                        }));

                        let prescriptionDetails = {
                            'pl': prescriptionList,
                            'pvd': this.prescriptionValidDays,
                            'sr': this.prescriptionSpecialReminder
                        };

                        this.axios.post('Prescription/UpdatePrescriptionListForUpload', {
                            'pi': this.prescriptionId,
                            'pd': prescriptionDetails
                        }).then(() => {
                            location.reload();
                        }).catch((error) => {
                            this.prescriptionSaveMessageIsError = true;
                            if (error.response) {
                                if (error.response.data) {
                                    this.prescriptionSaveMessage = error.response.data.message;
                                }
                            } else {
                                this.prescriptionSaveMessage = 'Error saving prescription list, please try again.';
                            }
                        });
                    }
                } else {
                    this.prescriptionSaveMessageIsError = true;
                    this.prescriptionSaveMessage = 'You have error(s) in the form. Please check the input(s) before saving...';
                    this.$nextTick(() => {
                        let errorInput = document.querySelector('.is-invalid');

                        if (errorInput) {
                            errorInput.focus();
                        }
                    });
                }
            });
        },
        validatePrescriptionValidDays () {
            return this.$validator.validateAll('prescription-valid-days');
        },
        backToPreviousPage () {
            window.history.back();
        }
    }

};

</script>

<style>

</style>
